'use client';

import React from 'react';
import {
  RadialBarChart, RadialBar, Legend, ResponsiveContainer, Tooltip, Cell
} from 'recharts';

interface PredictorSummaryChartProps {
  high: number;
  medium: number;
  low: number;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ name: string; value: number; fill: string }>;
}

function CustomTooltip({ active, payload }: CustomTooltipProps) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="card-base p-2.5 shadow-modal text-xs">
      <p className="font-bold text-foreground">{payload[0].name}</p>
      <p className="font-tabular text-muted-foreground">{payload[0].value} colleges</p>
    </div>
  );
}

export default function PredictorSummaryChart({ high, medium, low }: PredictorSummaryChartProps) {
  const total = high + medium + low;
  const data = [
    { name: 'High Chance', value: high, fill: '#16A34A' },
    { name: 'Medium Chance', value: medium, fill: '#D97706' },
    { name: 'Low Chance', value: low, fill: '#DC2626' },
  ].filter((d) => d.value > 0);

  return (
    <div className="flex items-center gap-4">
      <div className="flex-1 min-w-0" style={{ height: 160 }}>
        <ResponsiveContainer width="100%" height="100%">
          <RadialBarChart
            cx="50%"
            cy="50%"
            innerRadius="30%"
            outerRadius="90%"
            data={data}
            startAngle={90}
            endAngle={-270}
          >
            <RadialBar
              dataKey="value"
              background={{ fill: 'var(--muted)' }}
              cornerRadius={4}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-radial-${index}`} fill={entry.fill} />
              ))}
            </RadialBar>
            <Tooltip content={<CustomTooltip />} />
          </RadialBarChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <div className="shrink-0 space-y-3">
        {[
          { label: 'High Chance', count: high, color: '#16A34A', pct: total > 0 ? Math.round((high / total) * 100) : 0 },
          { label: 'Medium Chance', count: medium, color: '#D97706', pct: total > 0 ? Math.round((medium / total) * 100) : 0 },
          { label: 'Low Chance', count: low, color: '#DC2626', pct: total > 0 ? Math.round((low / total) * 100) : 0 },
        ].map((item) => (
          <div key={`legend-${item.label}`} className="flex items-center gap-2.5">
            <span className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: item.color }} />
            <div>
              <p className="text-xs font-semibold text-foreground">{item.label}</p>
              <p className="text-xs text-muted-foreground font-tabular">
                {item.count} colleges · {item.pct}%
              </p>
            </div>
          </div>
        ))}
        <div className="pt-2 border-t border-border">
          <p className="text-xs text-muted-foreground">Total analyzed</p>
          <p className="text-lg font-extrabold text-foreground font-tabular">{total}</p>
        </div>
      </div>
    </div>
  );
}